import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../hooks';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Status, UploadTypes, WebSocketDataReturnType, WebsocketStatusData } from '../../@types';
import { mergeSx, Styles } from '@t2c/shared-fe';
import { NextImage } from '@sdf/base';
import { CustomDialogContainer } from '../../Common/dialog';
import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

export const TaskNotifier = () => {
  const [taskNotifications, setTaskNotifications] = useState<Map<string, WebsocketStatusData>>(
    new Map()
  );
  const [previewData, setPreviewData] = useState<{
    data: any;
    type: WebSocketDataReturnType;
  } | null>(null);

  const {
    showAiGenModel,
    websocket: { aiGenerateTaskMap, handleRemoveFromTaskMap },
  } = useAppContext();

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!showAiGenModel) {
      const updatedMap = new Map(aiGenerateTaskMap);
      setTaskNotifications(updatedMap);
    }
  }, [aiGenerateTaskMap, showAiGenModel]);

  const handleCancel = (taskId: string, type: WebSocketDataReturnType) => {
    setTaskNotifications((prev) => {
      const updated = new Map(prev);
      updated.delete(taskId);
      return updated;
    });
    handleRemoveFromTaskMap({ id: taskId, type });
  };

  const handlePreview = (data: { data: any; type: WebSocketDataReturnType }, id: string) => {
    setPreviewData(data);
    handleCancel(id, data.type);
  };

  const handleCloseDialog = (taskId: string, type: WebSocketDataReturnType) => {
    setPreviewData(null);
    handleCancel(taskId, type);
  };

  const isPreviewDialogOpen = !!previewData;


  return (
    <>
      {Array.from(taskNotifications.values())
        .filter(({ status }) => status === Status.Success || status === Status.Error)
        .map(({ status, id, data, type }, index) => {
          const isSuccess = status === Status.Success;
          const genType = data?.aiMediaData?.type
          return (
            <Box
              key={id}
              sx={mergeSx(styles.taskContainer, {
                bottom: index * 10,
                zIndex: 1200 + index,
                marginTop: index === 0 ? 0 : -10,
                background: isSuccess
                  ? theme.palette.gradients.gradient5
                  : theme.palette.error.main,
              })}
            >
              <Stack 
                direction="row" 
                sx={{
                  ...styles.taskRow, 
                  gap: isSmall ? 2 : 10,
                }}
              >
                <Stack direction="row" sx={styles.taskDetails}>
                  <NextImage
                    src={isSuccess ? '/assets/icons/check.svg' : '/assets/icons/AlertIcon.svg'}
                    height={isSmall? 40 :60}
                    width={isSmall? 40 : 60}
                    alt=""
                  />
                  <Stack>
                    <Typography 
                      sx={{
                        ...styles.title,
                        fontSize: isSmall ? 12 : 26,
                      }}
                    >
                      {isSuccess ? `Your AI ${genType} is ready!` : `${genType} Generation Process Failed`}
                    </Typography>
                    <Typography 
                      sx={{
                        ...styles.subtitle,
                        fontSize: isSmall ? 11 : 15,
                      }}
                    >
                      {data?.aiMediaData?.name}
                    </Typography>
                  </Stack>
                </Stack>
                <Button
                  variant="contained"
                  sx={mergeSx(styles.viewMoreBtn, {
                    color: isSuccess ? theme.palette.primary.main : theme.palette.error.main,
                    fontSize: isSmall ? 12 : 16,
                    width: isSmall ? '30%' : '100%',
                  })}
                  onClick={() =>
                    isSuccess ? handlePreview({ data, type }, id) : handleCancel(id, type)
                  }
                >
                  {isSuccess ? 'View Now' : 'Close'}
                </Button>
              </Stack>
              <IconButton
                aria-label="close"
                onClick={() => handleCancel(id, type)}
                sx={styles.closeButton}
              >
                <CloseIcon sx={styles.closeIcon} />
              </IconButton>
            </Box>
          );
        })}

      {isPreviewDialogOpen && (
        <CustomDialogContainer
          open={isPreviewDialogOpen}
          maxWidth="sm"
          onClose={() => handleCloseDialog(previewData?.data?.id, previewData?.type)}
        >
          {previewData?.type === WebSocketDataReturnType.AI_GENERATE && (
            <Box sx={styles.dialogContentContainer}>
              {previewData?.data?.aiMediaData?.type === UploadTypes.Image ? (
                <Stack alignItems="center" my={4}>
                  <Avatar
                    src={previewData?.data?.aiMediaData?.url}
                    sx={{
                      borderRadius: 2,
                      height: '100%',
                      width: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Stack>
              ) : (
                <Stack alignItems="center">
                  <video
                    style={{
                      borderRadius: 8,
                      height: '100%',
                      width: '100%',
                      objectFit: 'cover',
                    }}
                    controls
                  >
                    <source src={previewData?.data?.aiMediaData?.url} type="video/mp4" />
                    <source src={previewData?.data?.aiMediaData?.url} type="video/quicktime" />
                    Your browser does not support this video.
                  </video>
                </Stack>
              )}
            </Box>
          )}
        </CustomDialogContainer>
      )}
    </>
  );
};

const styles: Styles = {
  taskContainer: {
    display: 'flex',
    flexDirection: 'row',
    py: 1,
    px: 2,
    width: '100%',
    position: 'fixed',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
  },
  taskRow: {
    alignItems: 'center',
    justifyContent: 'space-around',
    flexGrow: 1,
  },
  taskDetails: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.5,
  },
  title: {
    // fontSize: 26,
    fontWeight: 500,
    color: (theme) => theme.palette.common.white,
  },
  subtitle: {
    // fontSize: 15,
    fontWeight: 500,
    color: (theme) => theme.palette.common.white,
  },
  viewMoreBtn: {
    maxWidth: 300,
    height: '75%',
    // width: '100%',
    boxShadow: 'none',
    background: (theme) => theme.palette.common.white,
    '&:hover': {
      background: (theme) => theme.palette.common.white,
    },
  },
  closeButton: {
    color: (theme) => theme.palette.common.white,
  },
  closeIcon: {
    fontSize: 40,
  },
  dialogContentContainer: {
    maxWidth: '100%',
    overflow: 'hidden',
  },
};
